import { ChangeEvent, useRef, useState } from 'react';
import useImageUpload from '../../../../../../../hooks/useImageUpload';
import LangEnum from '../../../../../../../lang/LangEnum';
import snackbarStore from '../../../../../../../stores/Snackbar.store';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import { Catalog } from '../../../../../catalog.types';
import { CATALOG_COVER_DIMENSIONS } from '../../../../../creation/catalogCreationMedia.consts';
import { catalogsRepository } from '../../../../../repositories/catalogs.repository';

interface UseCatalogCardCoverUploadParams {
  catalog: Catalog;
  onCatalogUpdated: (catalog: Catalog) => void;
}

export const useCatalogCardCoverUpload = ({
  catalog,
  onCatalogUpdated,
}: UseCatalogCardCoverUploadParams) => {
  const { translate } = useTranslation();
  const { uploadImage } = useImageUpload(LangEnum.GLOBAL_ERROR_SENDING_IMAGE);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);

  const openFilePicker = () => {
    if (isUploadingCover) {
      return;
    }

    fileInputRef.current?.click();
  };

  const showGenericError = () => {
    snackbarStore.handleMessage({
      type: 'error',
      message: translate(LangEnum.GLOBAL_ERROR_MESSAGE),
    });
  };

  const uploadCover = async (file: File) => {
    const imageUrl = await uploadImage(file, CATALOG_COVER_DIMENSIONS.width);

    if (!imageUrl) {
      return;
    }

    const response = await catalogsRepository(catalog.id).update({ imageUrl }, {});

    if (!response.ok) {
      showGenericError();
      return;
    }

    onCatalogUpdated(response.data ?? { ...catalog, imageUrl });
  };

  const handleSelectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setIsUploadingCover(true);

    try {
      await uploadCover(file);
    } catch {
      showGenericError();
    } finally {
      setIsUploadingCover(false);
    }
  };

  return { fileInputRef, isUploadingCover, openFilePicker, handleSelectFile };
};
